<template>
    <div>
        <v-autocomplete
            ref="input"
            :model-value="city"
            :items="entries"
            :loading="baseLoading || loading"
            hide-no-data
            hide-selected
            item-value="id"
            item-title="id"
            :density="dense ? 'compact' : 'comfortable'"
            :label="baseLabel || $t(label)"
            :placeholder="showPlaceholder ? baseLabel || $t(label) : ''"
            :no-filter="noFilter"
            :clearable="true"
            return-object
            :single-line="singleLine"
            variant="outlined"
            :disabled="disabled"
            :hide-details="!showDetails && !tryResearch.status"
            :error="tryResearch.status"
            :error-messages="tryResearch.status ? [tryResearch.message] : []"
            :bg-color="backgroundColor"
            :prepend-inner-icon="showIcon ? prependInnerIcon : null"
            :rules="rules"
            :class="textFieldClass"
            :rounded="rounded"
            :persistent-placeholder="persistentPlaceholder"
            :data-cy="`${label}-input`"
            :custom-filter="filter"
            theme="light"
            :menu-icon="entries.length ? '$dropdown' : ''"
            :hint="hint"
            :persistent-hint="persistentHint"
            @click:clear="clear"
            @click="showMobileAutocomplete($event)"
            @update:model-value="onInput"
            @update:search="onSearch"
            @update:focused="$event && clear()"
        >
            <template v-slot:item="data">
                <div class="d-flex flex-column" :class="data.props.groupStyleClasses">
                    <div v-if="data.props.header" class="font-weight-bold text-primary text-caption px-4 text--primary">
                        {{ data.props.header }}
                    </div>
                    <slot v-else name="item" :item="data.item">
                        <div :class="data.props.groupElementStyleClasses">
                            <v-list-item v-bind="data.props">
                                <template v-slot:title>
                                    <div class="d-flex align-center" :class="{'pl-6': data.item.raw.subLocation}">
                                        <v-icon size="small" class="me-2">
                                            {{ icon(data.item.raw.type) }}
                                        </v-icon>
                                        <span v-html="itemLabel(data.item.raw)" />
                                    </div>
                                </template>
                            </v-list-item>
                        </div>
                    </slot>
                </div>
            </template>
            <template v-slot:selection="{item}">
                <slot name="selection" :item="item">
                    <div class="full-width full-height d-flex">
                        <span style="line-height: 1.2rem">
                            {{ selectionLabel(item.raw) }}
                        </span>
                    </div>
                </slot>
            </template>
            <template v-slot:append-inner>
                <slot name="append-inner" />
            </template>
        </v-autocomplete>
        <v-dialog v-model="showDialog" fullscreen transition="dialog-bottom-transition" theme="light">
            <v-toolbar color="primary" class="d-flex justify-end card-toolbar">
                <v-toolbar-title>{{ $t(label) }}</v-toolbar-title>
                <v-btn icon="mdi-close" theme="dark" @click="showDialog = false" />
            </v-toolbar>
            <v-card class="d-flex flex-column fill-height">
                <v-card-text>
                    <v-text-field
                        ref="mobileInput"
                        v-model="locationNameSearch"
                        :prepend-inner-icon="showIcon ? prependInnerIcon : null"
                        :loading="loading"
                        :error="tryResearch.status"
                        :error-messages="tryResearch.status ? [tryResearch.message] : []"
                        variant="outlined"
                        @update:model-value="onSearch(locationNameSearch)"
                    >
                        <template v-slot:append>
                            <v-icon v-if="locationNameSearch.length > 1" @click="locationNameSearch = ''">
                                mdi-close
                            </v-icon>
                        </template>
                    </v-text-field>
                    <v-list class="overflow-y-auto">
                        <v-list-item
                            v-for="(item, i) in entries"
                            :key="i"
                            class="d-flex justify-start align-center"
                            :class="{'pl-16': item.subLocation}"
                            :prepend-icon="icon(item.type)"
                            @click="selectCity(item)"
                        >
                            <div class="font-weight-regular d-block" v-html="itemLabel(item)" />
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'

    //TODO Improve mobile UX
    @Component
    export class CityAutocomplete extends Vue {
        @VModel({default: null}) city
        @Prop() rules
        @Prop({default: false, type: Boolean}) outlined
        @Prop({default: true, type: Boolean}) singleLine
        @Prop({default: false, type: Boolean}) filled
        @Prop({default: () => []}) defaultCities
        // TODO [P3] Need refactoring autocomplete all params - make it same base v-autocomplete
        @Prop({default: 'destination'}) label
        @Prop() baseLabel
        @Prop({default: true}) showPlaceholder
        @Prop({default: true}) showIcon
        @Prop({default: false, type: Boolean}) disabled
        @Prop() countryId
        @Prop({default: false, type: Boolean}) dense
        @Prop() productType
        @Prop({default: false, type: Boolean}) showDetails
        @Prop({default: 'mdi-map-marker'}) prependInnerIcon
        @Prop({default: 'white'}) backgroundColor
        @Prop({default: undefined}) rounded
        @Prop({default: false, type: Boolean}) persistentPlaceholder
        @Prop({default: false, type: Boolean}) baseLoading
        @Prop({default: () => {}, type: Function}) filter
        @Prop({default: true}) noFilter
        @Prop() locationType
        @Prop() hint: string
        @Prop({default: false, type: Boolean}) persistentHint: boolean

        entries = []
        loading = false
        search = null
        tryResearch = {
            status: false,
            message: '',
        }
        timer = null
        showDialog = false
        locationNameSearch = ''
        cancelTokenSource = null

        mounted() {
            this.load()
        }

        load() {
            if (!this.search && !this.city && this.entries.length === 0) {
                this.entries = this.defaultCities
            }
            if (this.city) {
                this.entries = [...this.entries, this.city]
            }
        }

        onInput(value) {
            this.city = value
            this.$refs.input.blur()
            if (this.tryResearch.status) {
                this.tryResearch.status = false
            }
        }

        clear() {
            this.city = null
            this.tryResearch.status = false
            this.entries = this.defaultCities
        }

        async showMobileAutocomplete(event) {
            if (this.$breakpoint.smAndDown) {
                this.showDialog = true
                this.$refs.input.blur()
                await this.$nextTick()
                setTimeout(() => {
                    this.$refs.mobileInput.focus()
                })
                event.stopPropagation()
            }
        }

        async selectCity(cityItem) {
            this.city = cityItem
            this.showDialog = false
            this.locationNameSearch = ''
        }

        itemLabel(entry) {
            return (
                `${entry.name}, ${entry.parentName}` +
                (entry.parentName !== entry.countryName ? `, ${entry.countryName}` : '')
            )
        }

        selectionLabel(entry) {
            return this.itemLabel(entry)
        }

        async onSearch(val) {
            await this.$nextTick()
            if (this.city) return
            this.search = val
            await this.initSearch(val)
        }

        async searchCities(val, checkTryResearch = true) {
            this.tryResearch.status = false
            const value = val.trim().toLowerCase()
            const rq = {
                pattern: `%${value}%`,
                limitCities: 10,
                orderBy: 'POPULARITY',
                ...(this.countryId && {countryId: this.countryId}),
                ...(this.productType && {productType: this.productType}),
                ...(this.productType === 'ACCOMMODATION' && {hotelsQuantity: 1}),
                ...(this.locationType && {locationType: this.locationType}),
            }

            const rs = await this.$api.locations.get(rq, this.cancelTokenSource.token)
            if (rs && rs.cities.length === 0 && checkTryResearch) {
                this.tryResearch.status = true
                this.tryResearch.message = this.isMobile
                    ? this.$t('try_rewrite_destination_mobile')
                    : this.$t('try_rewrite_destination')
            }

            return rs?.cities || []
        }

        async initSearch(val) {
            if ((this.entries.length > 0 && this.entries.indexOf(val) !== -1) || !val) return
            this.loading = true
            if (this.cancelTokenSource) this.cancelTokenSource.cancel('Operation canceled by the user.')
            this.cancelTokenSource = this.$axios.CancelToken.source()

            try {
                this.entries = await this.searchCities(val)
            } catch (e) {
                /* empty */
            } finally {
                this.loading = false
            }
        }

        @Watch('city', {immediate: true})
        onChangeValue(val) {
            if (val && Object.keys(val).length && !this.entries.find(({id}) => id === val.id)) {
                this.entries = [val]
            } else if (!val || !Object.keys(val).length) {
                this.entries = this.defaultCities
            }
        }

        get locationIcon() {
            return 'mdi-earth'
        }

        icon(type) {
            return this.locationIcon
        }

        get textFieldClass() {
            return this.$breakpoint && this.$breakpoint.smAndDown ? 'text-caption' : ''
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default CityAutocomplete
</script>

<style scoped lang="scss">
    :deep(.v-dialog) {
        overflow-y: hidden;
    }
    :deep(.v-label) {
        font-size: inherit;
    }

    :deep(.v-autocomplete__selection) {
        line-height: unset !important;
        height: unset !important;
        display: flex;
        align-items: start;
    }
</style>
